import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Video, ImageIcon, Trash2, PlayCircle, BookOpen } from "lucide-react";
import { PageHeader } from "@/components/app/PageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 1024 * 1024; // 1MB chunks to stay under proxy limits

function mediaUrl(m) {
  return `${BACKEND_URL}${m.file_url}`;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

// ---------- Shared gallery ----------
export function MediaGallery({ media, onDelete, canDelete }) {
  const { lang } = useI18n();
  const [preview, setPreview] = useState(null);

  if (media.length === 0) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          {lang === "ms" ? "Belum ada video atau gambar perkembangan." : "No progress videos or photos yet."}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {media.map((m) => (
          <Card key={m.id} className="overflow-hidden rounded-2xl" data-testid={`media-card-${m.id}`}>
            <button
              type="button"
              onClick={() => setPreview(m)}
              className="relative block h-44 w-full overflow-hidden bg-muted text-left"
              data-testid={`media-open-${m.id}`}
            >
              {m.media_type === "photo" ? (
                <img src={mediaUrl(m)} alt={m.title} className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" />
              ) : (
                <>
                  <video src={`${mediaUrl(m)}#t=0.5`} className="h-full w-full object-cover" preload="metadata" muted />
                  <span className="absolute inset-0 grid place-items-center bg-black/30">
                    <PlayCircle className="h-12 w-12 text-white drop-shadow" />
                  </span>
                </>
              )}
              <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">
                {m.media_type === "photo" ? <ImageIcon className="h-3 w-3" /> : <Video className="h-3 w-3" />}
                {m.media_type === "photo" ? ("Foto") : "Video"}
              </span>
            </button>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-medium">{m.title}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {m.student_name} · {new Date(m.created_at).toLocaleDateString()}
                  </div>
                  {m.description && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{m.description}</p>}
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {lang === "ms" ? "Oleh" : "By"} {m.uploaded_by_name} {m.file_size ? `· ${fmtSize(m.file_size)}` : ""}
                  </div>
                </div>
                {canDelete && canDelete(m) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive"
                    onClick={() => onDelete(m)}
                    data-testid={`media-del-${m.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle>{preview.title}</DialogTitle>
              </DialogHeader>
              <div className="overflow-hidden rounded-xl bg-black">
                {preview.media_type === "photo" ? (
                  <img src={mediaUrl(preview)} alt={preview.title} className="max-h-[70vh] w-full object-contain" />
                ) : (
                  <video src={mediaUrl(preview)} controls autoPlay className="max-h-[70vh] w-full" data-testid="media-video-player" />
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                {preview.student_name} · {new Date(preview.created_at).toLocaleString()}
                {preview.description && <p className="mt-1">{preview.description}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------- Teacher / staff / admin upload page ----------
export function TeacherProgressPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [students, setStudents] = useState([]);
  const [media, setMedia] = useState([]);
  const [filterStudent, setFilterStudent] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", title: "", description: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const fileRef = useRef(null);

  const load = () => api.get("/progress").then((r) => setMedia(r.data.media)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/students").then((r) => setStudents(r.data.students)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filterStudent === "all" ? media : media.filter((m) => m.student_id === filterStudent)),
    [media, filterStudent],
  );

  const doUpload = async () => {
    if (!file) return toast.error(lang === "ms" ? "Sila pilih fail video atau gambar" : "Please choose a video or photo file");
    if (!form.student_id) return toast.error(lang === "ms" ? "Sila pilih pelajar" : "Please select a student");
    if (!form.title.trim()) return toast.error(lang === "ms" ? "Sila isi tajuk" : "Please enter a title");

    const mediaType = file.type.startsWith("video") ? "video" : "photo";
    setUploading(true);
    setProgressPct(1);
    try {
      // 1) init
      const initFd = new FormData();
      initFd.append("filename", file.name);
      initFd.append("media_type", mediaType);
      const { data: init } = await api.post("/progress/upload/init", initFd);
      const uploadId = init.upload_id;

      // 2) chunks
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("chunk", blob, `chunk_${i}`);
        await api.post("/progress/upload/chunk", fd);
        setProgressPct(Math.round(((i + 1) / totalChunks) * 95));
      }

      // 3) complete
      await api.post("/progress/upload/complete", {
        upload_id: uploadId,
        student_id: form.student_id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        media_type: mediaType,
      });
      setProgressPct(100);
      toast.success(lang === "ms" ? "Berjaya dimuat naik!" : "Uploaded successfully!");
      setOpen(false);
      setForm({ student_id: "", title: "", description: "" });
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      setProgressPct(0);
    }
  };

  const del = async (m) => {
    if (!window.confirm(lang === "ms" ? "Padam media ini?" : "Delete this media?")) return;
    try {
      await api.delete(`/progress/${m.id}`);
      toast.success(lang === "ms" ? "Dipadam" : "Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="teacher-progress">
      <PageHeader
        eyebrow={lang === "ms" ? "Perkembangan" : "Progress"}
        title={lang === "ms" ? "Perkembangan Pelajar" : "Student Progress"}
        subtitle={lang === "ms"
          ? "Muat naik video latihan membaca atau gambar buku latihan untuk dikongsi dengan ibu bapa."
          : "Upload reading practice videos or workbook photos to share with parents."}
        action={
        <Dialog open={open} onOpenChange={(o) => !uploading && setOpen(o)}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105" data-testid="upload-media-btn">
              <Upload className="h-4 w-4" /> {lang === "ms" ? "Muat naik" : "Upload"}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{lang === "ms" ? "Muat naik perkembangan" : "Upload progress"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{lang === "ms" ? "Pelajar" : "Student"}</Label>
                <Select value={form.student_id || undefined} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger className="h-11" data-testid="up-student">
                    <SelectValue placeholder={lang === "ms" ? "Pilih pelajar" : "Select student"} />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.full_name} — {s.level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{lang === "ms" ? "Tajuk" : "Title"}</Label>
                <Input
                  className="h-11"
                  data-testid="up-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={lang === "ms" ? "cth: Latihan membaca — Bulan 2" : "e.g. Reading practice — Month 2"}
                />
              </div>
              <div>
                <Label>{lang === "ms" ? "Catatan (pilihan)" : "Notes (optional)"}</Label>
                <Textarea
                  data-testid="up-desc"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={lang === "ms" ? "Kemajuan bacaan suku kata sudah lancar…" : "Reading progress notes…"}
                  rows={3}
                />
              </div>
              <div>
                <Label>{lang === "ms" ? "Fail video / gambar" : "Video / photo file"}</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/*,image/*"
                  data-testid="up-file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 block w-full cursor-pointer rounded-xl border border-border/70 bg-muted/30 p-3 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-primary file:px-4 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground"
                />
                {file && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {file.name} · {fmtSize(file.size)} · {file.type.startsWith("video") ? "Video" : "Foto"}
                  </p>
                )}
              </div>
              {uploading && (
                <div className="space-y-1">
                  <Progress value={progressPct} />
                  <p className="num text-center text-xs text-muted-foreground">{progressPct}%</p>
                </div>
              )}
              <Button onClick={doUpload} disabled={uploading} className="h-11 w-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display font-extrabold uppercase tracking-wide text-white" data-testid="up-submit">
                {uploading
                  ? (lang === "ms" ? "Memuat naik…" : "Uploading…")
                  : (lang === "ms" ? "Muat naik" : "Upload")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        }
      />

      <div className="max-w-xs">
        <Select value={filterStudent} onValueChange={setFilterStudent}>
          <SelectTrigger className="h-11 rounded-xl" data-testid="filter-student">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "ms" ? "Semua pelajar" : "All students"}</SelectItem>
            {students.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <MediaGallery
        media={filtered}
        onDelete={del}
        canDelete={(m) => user?.role === "admin" || m.uploaded_by === user?.id}
      />
    </div>
  );
}

// ---------- Parent view page ----------
export function ParentProgressPage() {
  const { lang } = useI18n();
  const [media, setMedia] = useState([]);
  const [kids, setKids] = useState([]);
  const [filterKid, setFilterKid] = useState("all");

  useEffect(() => {
    api.get("/progress").then((r) => setMedia(r.data.media)).catch(() => {});
    api.get("/students").then((r) => setKids(r.data.students)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filterKid === "all" ? media : media.filter((m) => m.student_id === filterKid)),
    [media, filterKid],
  );

  return (
    <div className="space-y-6" data-testid="parent-progress">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={lang === "ms" ? "Perkembangan Anak" : "Child Progress"}
        subtitle={lang === "ms"
          ? "Video latihan membaca dan gambar buku latihan anak anda, dikongsi oleh cikgu."
          : "Reading practice videos and workbook photos of your child, shared by the teacher."}
      />

      {kids.length > 1 && (
        <div className="max-w-xs">
          <Select value={filterKid} onValueChange={setFilterKid}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="parent-filter-kid">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === "ms" ? "Semua anak" : "All children"}</SelectItem>
              {kids.map((k) => (
                <SelectItem key={k.id} value={k.id}>{k.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {media.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <BookOpen className="h-10 w-10 text-primary" />
            <p className="text-sm text-muted-foreground">
              {lang === "ms"
                ? "Belum ada perkongsian daripada cikgu. Video dan gambar perkembangan anak anda akan dipaparkan di sini."
                : "Nothing shared by the teacher yet. Your child's progress videos and photos will appear here."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <MediaGallery media={filtered} />
      )}
    </div>
  );
}
